import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCheckCircle, FaClock, FaDraftingCompass, FaMapMarkerAlt, FaTools } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import ChannelLetterGuide from '../components/ChannelLetterGuide';
import GoogleReviews from '../components/GoogleReviews';
import Contact from '../components/Contact';
import Footer from '../components/Footer';
import FloatingContactWidget from '../components/FloatingContactWidget';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import projectGroups from '../data/projectGroups';

const featuredSlugs = ['new-life-center-channel-letters', 'las-mercedes-channel-letter-installation'];

const copy = {
    en: {
        eyebrow: 'CHANNEL LETTER SIGNS IN HOUSTON',
        title: 'Custom Channel Letters That Put Your Business in the Spotlight',
        intro: 'NJ Design & Print designs, fabricates, installs, maintains and repairs illuminated channel letter signs for businesses throughout Houston and surrounding areas.',
        quote: 'Request a Free Quote',
        projects: 'See Channel Letter Projects',
        highlights: [
            ['Permit Assistance', 'We help identify and coordinate the sign approvals required for your property and local jurisdiction.'],
            ['Typical Fabrication', 'Most channel letters are fabricated in approximately 2–4 weeks after final design approval.'],
            ['Complete Maintenance', 'LED, electrical, faces, trim, mounting, cleaning, sealing and general channel letter repairs.'],
            ['Houston Area Service', 'Professional site visits and installation throughout Houston and surrounding communities.']
        ],
        timingTitle: 'A Realistic Project Timeline',
        timingText: 'Fabrication normally takes 2–4 weeks after design approval. The complete schedule can be longer when permits, landlord approval, custom materials, electrical preparation or complex installation conditions are involved. Your quote includes a project-specific timeline.',
        permitTitle: 'Help With Permits and Approvals',
        permitText: 'Sign requirements vary by city, property and shopping center. We review the available project information, help coordinate required documentation and keep you informed before fabrication and installation move forward.',
        workEyebrow: 'BUILT AND INSTALLED BY NJ DESIGN & PRINT',
        workTitle: 'Channel Letter Work in Houston',
        workText: 'Real projects showing the kind of visibility, fabrication and installation we provide for local businesses.',
        viewProject: 'View project',
        finalTitle: 'Ready to Make Your Storefront More Visible?',
        finalText: 'Tell us about your building, logo and preferred lighting style. We will help you choose the right channel letter solution.',
        finalCta: 'Start My Channel Letter Project'
    },
    es: {
        eyebrow: 'CHANNEL LETTERS EN HOUSTON',
        title: 'Channel Letters personalizados que hacen destacar tu negocio',
        intro: 'NJ Design & Print diseña, fabrica, instala, mantiene y repara channel letters iluminados para negocios de Houston y sus alrededores.',
        quote: 'Solicitar cotización gratis',
        projects: 'Ver proyectos de Channel Letters',
        highlights: [
            ['Ayuda con permisos', 'Ayudamos a identificar y coordinar las aprobaciones requeridas para tu propiedad y jurisdicción local.'],
            ['Fabricación habitual', 'La mayoría de los channel letters se fabrican aproximadamente en 2–4 semanas después de aprobar el diseño final.'],
            ['Mantenimiento completo', 'LED, sistema eléctrico, caras, bordes, montaje, limpieza, sellado y reparaciones generales.'],
            ['Servicio en Houston', 'Visitas e instalación profesional en Houston y comunidades de los alrededores.']
        ],
        timingTitle: 'Un plazo realista para tu proyecto',
        timingText: 'La fabricación normalmente toma de 2 a 4 semanas después de aprobar el diseño. El proceso completo puede extenderse si requiere permisos, aprobación del propietario, materiales especiales, preparación eléctrica o una instalación compleja. Tu cotización incluirá un calendario específico.',
        permitTitle: 'Ayuda con permisos y aprobaciones',
        permitText: 'Los requisitos cambian según la ciudad, propiedad y centro comercial. Revisamos la información disponible, ayudamos a coordinar la documentación necesaria y te mantenemos informado antes de fabricar e instalar.',
        workEyebrow: 'FABRICADOS E INSTALADOS POR NJ DESIGN & PRINT',
        workTitle: 'Trabajos de Channel Letters en Houston',
        workText: 'Proyectos reales que muestran la visibilidad, fabricación e instalación que ofrecemos a negocios locales.',
        viewProject: 'Ver proyecto',
        finalTitle: '¿Listo para darle más visibilidad a tu negocio?',
        finalText: 'Cuéntanos sobre tu edificio, logotipo y estilo de iluminación. Te ayudaremos a elegir la solución adecuada.',
        finalCta: 'Comenzar mi proyecto'
    }
};

const icons = [FaDraftingCompass, FaClock, FaTools, FaMapMarkerAlt];

const ChannelLettersPage = () => {
    const { language } = useLanguage();
    const { isDark } = useTheme();
    const text = copy[language] || copy.en;
    const projects = featuredSlugs.map((slug) => projectGroups.find((project) => project.slug === slug)).filter(Boolean);

    useEffect(() => {
        const previousTitle = document.title;
        const description = document.querySelector('meta[name="description"]');
        const previousDescription = description?.getAttribute('content');
        document.title = language === 'es' ? 'Channel Letters en Houston | NJ Design & Print' : 'Channel Letter Signs Houston | NJ Design & Print';
        description?.setAttribute('content', language === 'es'
            ? 'Diseño, fabricación, instalación, mantenimiento y reparación de channel letters en Houston y alrededores.'
            : 'Custom channel letter design, fabrication, installation, maintenance and repair in Houston and surrounding areas.');
        return () => {
            document.title = previousTitle;
            if (description && previousDescription) description.setAttribute('content', previousDescription);
        };
    }, [language]);

    return (
        <div className={`min-h-screen overflow-x-hidden ${isDark ? 'bg-gray-950' : 'bg-white'}`}>
            <Navbar />
            <main>
                <section className="relative min-h-[720px] flex items-center pt-28 pb-20 px-4 overflow-hidden bg-black">
                    <video autoPlay muted loop playsInline poster="/videos/channel-letter-installation-poster.jpg" className="absolute inset-0 w-full h-full object-cover opacity-45">
                        <source src="/videos/channel-letter-installation.mp4" type="video/mp4" />
                    </video>
                    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/35" />
                    <div className="relative max-w-7xl mx-auto w-full">
                        <div className="max-w-4xl">
                            <p className="text-green-yellow-primary font-black tracking-widest text-sm mb-4">{text.eyebrow}</p>
                            <h1 className="text-white text-4xl sm:text-5xl lg:text-7xl font-black leading-tight mb-6">{text.title}</h1>
                            <p className="text-gray-100 text-lg sm:text-xl leading-relaxed max-w-3xl mb-9">{text.intro}</p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/contact" className="inline-flex justify-center items-center gap-3 rounded-full bg-gradient-to-r from-atlantis to-green-yellow-primary px-8 py-4 text-black font-black">{text.quote} <FaArrowRight /></Link>
                                <a href="#channel-letter-projects" className="inline-flex justify-center items-center rounded-full border-2 border-white px-8 py-4 text-white font-black hover:border-atlantis">{text.projects}</a>
                            </div>
                        </div>
                    </div>
                </section>

                <section className={`py-16 px-4 ${isDark ? 'bg-gray-950' : 'bg-white'}`}>
                    <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {text.highlights.map(([title, description], index) => {
                            const Icon = icons[index];
                            return <article key={title} className={`rounded-2xl border p-6 shadow-lg ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-green-100'}`}>
                                <Icon className="text-3xl text-atlantis mb-4" />
                                <h2 className={`text-xl font-black mb-3 ${isDark ? 'text-white' : 'text-saratoga'}`}>{title}</h2>
                                <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>{description}</p>
                            </article>;
                        })}
                    </div>
                </section>

                <ChannelLetterGuide />

                <section className={`py-20 px-4 ${isDark ? 'bg-gray-950' : 'bg-white'}`}>
                    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8">
                        <article className={`rounded-3xl p-8 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-green-50 border-green-200'}`}>
                            <FaClock className="text-4xl text-atlantis mb-5" />
                            <h2 className={`text-3xl font-black mb-4 ${isDark ? 'text-white' : 'text-saratoga'}`}>{text.timingTitle}</h2>
                            <p className={`text-lg leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{text.timingText}</p>
                        </article>
                        <article className="rounded-3xl p-8 bg-gradient-to-br from-atlantis to-green-yellow-primary text-black">
                            <FaCheckCircle className="text-4xl mb-5" />
                            <h2 className="text-3xl font-black mb-4">{text.permitTitle}</h2>
                            <p className="text-lg leading-relaxed font-medium">{text.permitText}</p>
                        </article>
                    </div>
                </section>

                <section id="channel-letter-projects" className={`py-20 px-4 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center max-w-3xl mx-auto mb-12">
                            <p className="text-atlantis font-black tracking-widest text-sm mb-3">{text.workEyebrow}</p>
                            <h2 className={`text-4xl md:text-5xl font-black mb-5 ${isDark ? 'text-white' : 'text-saratoga'}`}>{text.workTitle}</h2>
                            <p className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{text.workText}</p>
                        </div>
                        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                            {projects.map((project) => <Link key={project.slug} to={`/portfolio/${project.slug}`} className={`group overflow-hidden rounded-3xl border shadow-xl ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
                                <img src={project.media[0].type === 'video' ? project.media[0].poster : project.media[0].src} alt={`${project.title} channel letter project`} className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                                <div className="p-7">
                                    <p className="text-atlantis font-bold text-sm mb-2">Houston, Texas</p>
                                    <h3 className={`text-2xl font-black mb-3 ${isDark ? 'text-white' : 'text-saratoga'}`}>{project.title}</h3>
                                    <p className={`mb-5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{project.summary[language] || project.summary.en}</p>
                                    <span className="inline-flex items-center gap-2 text-atlantis font-black">{text.viewProject} <FaArrowRight /></span>
                                </div>
                            </Link>)}
                        </div>
                    </div>
                </section>

                <GoogleReviews />

                <section className="py-16 px-4 bg-gradient-to-r from-atlantis to-green-yellow-primary text-black text-center">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="text-4xl md:text-5xl font-black mb-5">{text.finalTitle}</h2>
                        <p className="text-lg font-medium mb-8">{text.finalText}</p>
                        <Link to="/contact" className="inline-flex items-center gap-3 rounded-full bg-black text-white px-8 py-4 font-black">{text.finalCta} <FaArrowRight /></Link>
                    </div>
                </section>

                <Contact />
            </main>
            <Footer />
            <FloatingContactWidget />
        </div>
    );
};

export default ChannelLettersPage;
